import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Screen } from '@ghostscreen/contracts';
import { getCardConnectionStatus } from '../../../lib/dashboard/screenStatus';
import { ScreenManagePanel } from '../ScreenManagePanel';

type DisplayManageDesktopSheetProps = {
  organisationId: string;
  screen: Screen;
  playlists: { id: string; name: string }[];
  startPlayContent?: boolean;
  onClose: () => void;
};

/** Desktop/large-layout manage surface — near-fullscreen sheet over the board. */
export function DisplayManageDesktopSheet({
  organisationId,
  screen,
  playlists,
  startPlayContent = false,
  onClose,
}: DisplayManageDesktopSheetProps) {
  const { t } = useTranslation(['displays', 'common']);
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('manage.aria', { name: screen.name })}
      className="fixed inset-0 z-[100]"
      style={{ background: 'color-mix(in srgb, #0a0a0b 55%, transparent)' }}
      onClick={onClose}
    >
      <div
        className="absolute inset-x-0 bottom-0 top-2 flex flex-col overflow-hidden rounded-t-[16px] border border-b-0 border-line bg-panel shadow-lg sm:top-3 sm:rounded-t-[20px]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-3.5 sm:px-6 sm:py-4">
          <h2 className="m-0 text-base font-bold text-ink">
            {t('manage.sheetTitle', { name: screen.name })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-muted hover:text-ink"
          >
            {t('common:actions.close')}
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 pb-8 sm:p-6 sm:pb-10">
          <ScreenManagePanel
            organisationId={organisationId}
            screen={screen}
            playlists={playlists}
            unpaired={getCardConnectionStatus(screen) === 'unpaired'}
            startPlayContent={startPlayContent}
            onDeleted={onClose}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
